import { theme as defaultTheme, Button } from "@mindlercare/ui-web";
import { useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import {
  endOfDay,
  format,
  fromUnixTime,
  getUnixTime,
  startOfDay,
  sub,
} from "date-fns";
import PageHeader from "components/lib/pageHeader";
import CONSTANTS from "core/constants";
import { Filters } from "../shared/Filters";
import Table from "../shared/tableBackendFilter";
import { Filter } from "../shared/types";
import { getIcbtPaymentsInDateRange } from "./api";
import {
  formatIcbtPaymentTableData,
  getIcbtPaymentTableColumns,
  getIcbtPaymentTableFilters,
  getIcbtPaymentTableSensitiveFields,
} from "./model";
import { IcbtPaymentData } from "./types";

export const IcbtPayments = () => {
  const { t } = useTranslation();
  const csvLinkButton = useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const [fromDate, setFromDate] = useState<number>(
    getUnixTime(startOfDay(sub(new Date(), { days: 10 }))),
  );
  const [toDate, setToDate] = useState<number>(
    getUnixTime(endOfDay(new Date())),
  );
  const [icbtPayments, setIcbtPayments] = useState<IcbtPaymentData[]>([]);

  const formatToCSV = (data: IcbtPaymentData[]) =>
    data.map((d) => ({
      id: d.answerId,
      date: format(
        new Date(d.createdAt),
        CONSTANTS.DATE_FORMAT.YEAR_NAME_DATE_SECONDS_FORMAT,
      ),
      firstName: d.firstName,
      lastName: d.lastName,
      nationalIdentity: d.nationalIdentity,
    }));

  const getCSVFileName = () =>
    `Mindler_iCBT_fee_${format(fromUnixTime(fromDate), CONSTANTS.DATE_FORMAT.DEFAULT_DATE_FORMAT)}-${format(fromUnixTime(toDate), CONSTANTS.DATE_FORMAT.DEFAULT_DATE_FORMAT)}.csv`;

  const handleApplyFilters = () => {
    getIcbtPaymentsInDateRange(fromDate, toDate)
      .then((res) => {
        setIcbtPayments(res.data);
      })
      .catch(() => {
        setIcbtPayments([]);
      });
  };

  const handleChangeFilter = (id: string, rawValue: string) => {
    if (id === "fromDate") {
      setFromDate(getUnixTime(new Date(rawValue)));
    } else {
      setToDate(getUnixTime(new Date(rawValue)));
    }
  };

  return (
    <Root>
      <PageHeader translation={t("ICBT_PAYMENTS")} hasSensitive={false} />
      <Filters
        filters={
          getIcbtPaymentTableFilters(t, { fromDate, toDate }) as Filter[]
        }
        onApplyFilters={handleApplyFilters}
        onChangeFilter={handleChangeFilter}
      />

      <Table
        tableColumns={getIcbtPaymentTableColumns(t)}
        rows={formatIcbtPaymentTableData(icbtPayments)}
        rowsPerPage={1000}
        sensitiveFields={getIcbtPaymentTableSensitiveFields()}
      >
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <div>
            <Button
              variant="secondary"
              onClick={() => csvLinkButton.current?.link.click()}
            >
              {t("EXTRACT_REPORT")}
            </Button>
            <NativeCSVButton
              data={formatToCSV(icbtPayments)}
              filename={getCSVFileName()}
              ref={csvLinkButton}
            >
              {t("EXTRACT_REPORT")}
            </NativeCSVButton>
          </div>
        </div>
      </Table>
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  background-color: ${(p) => defaultTheme.COLORS.WHITE};
  padding: ${(p) => defaultTheme.PADDING.XL};
  flex-direction: column;
`;

const NativeCSVButton = styled(CSVLink)`
  display: none;
`;
